import { clearPendingPassword } from "@/lib/pendingPassword";
import "react-calendar/dist/Calendar.css";
// Font Awesome icons render as inline SVG (src/components/fa-icon); their CSS ships in the global
// stylesheet so server-rendered icons are sized before hydration, instead of being injected at runtime.
import "@fortawesome/fontawesome-svg-core/styles.css";
import "./globals.scss";
import "./redesign.scss";
import "dayjs/locale/en-gb";

import { AppProps } from "next/app";
import { ComponentType, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { setLogosTheme } from "@/lib/logos";
import { useIdle } from "react-use";
import { useRouter } from "next/router";
import { registerSiteTools } from "@/lib/webmcp";
import { scheduleIdle } from "@/lib/defer";
import dynamic from "next/dynamic";
import { isUndefined } from "lodash";
import axios from "axios";
import { installGetDeduplication } from "@/lib/dedupe-requests";
import { legacyParamsSerializer, xsrfForApi } from "@/lib/axios-compat";
import { rateLimitMessage, retryAfterSeconds, SUPPORT_EMAIL, TERMS_URL } from "@/lib/rateLimitMessage";
import { isSignedInOnlyRoute } from "@/lib/userCallFailure";
import { hasInflightOAuth } from "@/lib/inflightOAuth";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isToday from "dayjs/plugin/isToday";
import duration from "dayjs/plugin/duration";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import isYesterday from "dayjs/plugin/isYesterday";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import weekday from "dayjs/plugin/weekday";
import { Context } from "@/lib/Context";
import Head from "next/head";
import RootLayout from "@/layouts/root/RootLayout";
import AuthLayout from "@/layouts/auth/AuthLayout";
import BrandLoading from "@/components/brand-loading/BrandLoading";
import { LOADER_HELD_CLASS } from "@/lib/loaderHold";
import { useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { withApp } from "@/lib/app-hoc";
import {
    backgroundColorState,
    customViewsCurrentViewDataState,
    customViewsState,
    customViewsUnsavedState,
    darkerBackgroundColorState,
    darkSurfaceDefaultState,
    greyDarkerState,
    subtextGreyState,
    greyLighterState,
    greyMediumState,
    negativeState,
    neutralState,
    newbackgroundColorState,
    notificationsListState,
    ortexColorState,
    positiveState,
    stockTradingItemIdState,
    subscriptionOfferingsState,
    subscriptionState,
    textColorState,
    themeState,
    universesState,
    universeState,
    userState,
    whiteState,
    userDataState,
    showAppSubscriberModalState,
    showAgreementsModalState,
    greyState,
    chatModalState,
    contrastState,
    HIGH_CONTRAST_COLORS,
    moduleSettingsState,
    userColorSettingsState,
    isManualColor,
    UserColorKey,
    lighterOrtexColorState,
    blackMediumState,
    darkDefaultState,
    iconColorState,
    dividerDarkState,
    borderDarkState,
    popoverContentState,
    paymentModalOpenState,
} from "@/lib/store";
import { ThemeProvider } from "@mui/material/styles";
import { Backdrop, CircularProgress, CssBaseline, StyledEngineProvider } from "@mui/material";
import CustomViewsUnsavedChangesModal from "@/components/custom-views-unsaved-changes-modal/CustomViewsUnsavedChangesModal";
import { saveCustomViews } from "@/controllers/custom-views";
import { Universe } from "@/models/universe";
import { detectBot, isOrtexApp } from "@/lib/utils";
import { LicenseInfo } from "@mui/x-license";
import { signOutFromBackend } from "@/lib/signOutFromBackend";
import { setAuthEventHandler } from "@/lib/authEvents";
import { socialLogin } from "@/lib/socialLogin";
import { holdEarlyEvents, hostListening } from "@/lib/earlyEvents";
import { OPEN_MODAL_LINK_EVENT } from "src/hooks/useModalLink";
import GlobalTourLauncherHost from "@/components/tour/GlobalTourLauncher";
import useGetUserData from "src/hooks/useGetUserData";
import { saveCheckoutIntent, loadCheckoutIntent, clearCheckoutIntent, checkoutReplayHref } from "@/lib/checkoutIntent";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";
import SnackBarHost from "@/components/snack-bar/SnackBar";
import { getThemes } from "src/theme";
import { CONTRAST_SETTING_KEY, persistContrastSetting, readStoredContrast, storeContrast } from "@/lib/contrast";
import { useAppWebSockets } from "src/hooks/useAppWebSockets";
import { usePaymentConfig } from "src/hooks/usePaymentConfig";
import { usePushNotifications } from "src/hooks/usePushNotifications";
import { useAndroidBackButton } from "src/hooks/useAndroidBackButton";
import AppUrlListener from "@/components/app-url-listener/AppUrlListener";
import { useNotificationsFetcher } from "src/hooks/useNotificationsFetcher";
import { setTrackingUser, trackRouteView, trackSignUp } from "@/lib/tracking";
import { useTimeSettingsSync } from "@/lib/time/state";
import { brighten } from "@/lib/ortex-charts/color";

dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(isBetween);
dayjs.extend(isYesterday);
dayjs.extend(quarterOfYear);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(timezone);
dayjs.extend(weekday);

dayjs.locale("en-gb");

// MUI X Premium licence (annual, Q1-2026). It is a client-side key: MUI checks it in the browser and
// it ships in the bundle either way, so it lives here rather than in a Vercel variable.
LicenseInfo.setLicenseKey("82e5e0924c8be8b3a2cd21808645bba6Tz0xMjkzNjcsRT0xODEwODU3NTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9UTEtMjAyNixRPTEsQVQ9bXVsdGksS1Y9Mg==");

// The app-wide hosts below render from _app, and _app re-renders on every atom update it reads: five to
// seven times while a page starts up (the user, universes, subscription, notifications...). Memoized, a
// host re-renders only when its own props, state or atoms change; unmemoized, each of those _app renders
// re-rendered every dialog, the snackbar and the tooltip layer with it, 100-500 ms of a phone's main thread
// per render at start-up (ortex-gui#1349).
//
// None of them draws anything in the server HTML (each is a closed dialog, or waits for the user or for a
// window event), so they load after hydration (`ssr: false`) instead of with the shell: their code was
// evaluated in the shell's one start-up task on every page, before the page could respond (ortex-gui#1285).
// The window events the SEC, news and call dialogs open on are held until they listen (src/lib/earlyEvents.ts).
// The tour launcher stays in the shell: page tours listen for its start event too, so it cannot be replayed.
const OPEN_SEC_DOC_EVENT = "ortex:open-sec-doc"; // SecFilingModal's, kept here so the shell does not import it
holdEarlyEvents([OPEN_SEC_DOC_EVENT, `${OPEN_MODAL_LINK_EVENT}|news`, `${OPEN_MODAL_LINK_EVENT}|call`]);
const listening = <P extends object>(Host: ComponentType<P>, key: string) => {
    const Listening = (props: P) => {
        // Runs after the host's own effects, so its listener is in place when the held events are dispatched.
        useEffect(() => hostListening(key), []);
        return <Host {...props} />;
    };
    return Listening;
};
const AltDataUpgradeDialog = memo(dynamic(() => import("@/components/alt-data/AltDataUpgradeDialog"), { ssr: false }));
const TrialFullAccessNotice = memo(dynamic(() => import("@/components/trial-full-access-notice/TrialFullAccessNotice"), { ssr: false }));
const CancellationLossWarning = memo(dynamic(() => import("@/components/cancellation-loss-warning/CancellationLossWarning"), { ssr: false }));
const GlobalTourLauncher = memo(GlobalTourLauncherHost);
const GoogleOneTap = memo(dynamic(() => import("@/components/google-one-tap/GoogleOneTap"), { ssr: false }));
const SecFilingModal = memo(dynamic(() => import("@/components/sec-filing-modal/SecFilingModal").then((m) => listening(m.default, OPEN_SEC_DOC_EVENT)), { ssr: false }));
const NewsStoryModal = memo(dynamic(() => import("@/components/news-story-modal/NewsStoryModal").then((m) => listening(m.default, `${OPEN_MODAL_LINK_EVENT}|news`)), { ssr: false }));
const CallDetailsHost = memo(dynamic(() => import("@/components/call-details-modal/CallDetailsModal").then((m) => listening(m.CallDetailsHost, `${OPEN_MODAL_LINK_EVENT}|call`)), { ssr: false }));
const AgreementsModal = dynamic(() => import("@/components/agreements-modal/AgreementsModal"), { ssr: false });
// The app shells only.
const PlanPickerDrawer = dynamic(() => import("@/components/plan-picker-drawer/PlanPickerDrawer"), { ssr: false });
const PushPrePrompt = dynamic(() => import("@/components/push-notifications/PushPrePrompt"), { ssr: false });
const AppUpdateBanner = dynamic(() => import("@/components/app-update/AppUpdateBanner"), { ssr: false });
const SnackBar = memo(SnackBarHost);
// Its global styles are re-serialized on every render; the theme still reaches it through context.
const AppCssBaseline = memo(CssBaseline);

// Feedback while a modal's chunk downloads on first open; fast loads (under 200 ms) show nothing, and a failed load
// (offline, or a stale deploy) shows nothing rather than a backdrop that cannot be dismissed.
const ModalLoading = ({ pastDelay, error }: { pastDelay?: boolean; error?: Error | null }) => {
    return pastDelay && !error ? (
        <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal }}>
            <CircularProgress color="inherit" />
        </Backdrop>
    ) : null;
};
// Chat opens from a menu action, so the modal and the ortex-charts packages it renders load on first open instead of in the shared _app bundle.
const ChatModal = dynamic(() => import("@/components/chat-modal/ChatModal"), { ssr: false, loading: ModalLoading });
// Payment renders only after a Subscribe click on the web (never in the apps), so Stripe and the checkout UI load then
// rather than in the shell. The app-subscriber modal (web view of an account that subscribed in the apps) opens on its
// own once the subscription is known, so it needs no loading feedback.
const Payment = dynamic(() => import("@/components/payment/Payment"), { ssr: false, loading: ModalLoading });
const AppSubscriberModal = dynamic(() => import("@/components/app-subscriber-modal"), { ssr: false });

axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";
axios.defaults.validateStatus = (status) => status < 500;
axios.defaults.baseURL = process.env.NEXT_PUBLIC_API_URL;
axios.defaults.paramsSerializer = legacyParamsSerializer;
axios.defaults.withXSRFToken = xsrfForApi(process.env.NEXT_PUBLIC_API_URL);
axios.defaults.withCredentials = !!process.env.NEXT_PUBLIC_VERCEL_ENV;
axios.defaults.params = { GUIv: 2, authid: process.env.NEXT_PUBLIC_VERCEL_ENV ? undefined : "ortex" };

// Several pages mount a component more than once and fire the same GET two or three times
// within a few milliseconds. They overlap, so no HTTP cache collapses them and the backend
// does the work twice. This shares one round trip between concurrent identical GETs; see
// the module for what it deliberately does not do.
installGetDeduplication();

const FOUR_HOURS = 14400000;

// The sign-in / create-account pages render their form at once instead of under the full-screen
// loader. The loader waited for the user + universes requests (2-3 s), which a signed-out visitor
// does not need; a visitor who already holds a session still gets it while AuthPage forwards them.
const AUTH_FORM_ROUTES = ["/login", "/register"];

const hasStoredCognitoSession = (): boolean => {
    try {
        return Object.keys(localStorage).some((key) => key.startsWith("CognitoIdentityServiceProvider") && key.endsWith(".accessToken")) || (isOrtexApp() && !!localStorage.getItem("ortexUser"));
    } catch {
        return false;
    }
};

// Pages that send a signed-out visitor elsewhere once their user has loaded (SIGNED_IN_ONLY_ROUTES, shared with
// useGetUserData): their server HTML is never what a visitor gets, so the full-page loader stays over it
// (src/lib/loaderHold.ts).

const PUBLIC_ROUTES = ["/login", "/login-legacy", "/cognito-login", "/register", "/iframes/", "/auth-redirect", "/apis", "/apis/plans", "/apis/credits", "/c/[slug]"];

// Per-session marker so the persisted checkout intent (src/lib/checkoutIntent.ts)
// is re-applied at most once per session instead of on every navigation.
const CHECKOUT_SHOWN_KEY = "ortex_checkout_shown";

// Plans that mean "already paying". Registered free users are "ORTEX Free" and
// anonymous visitors "ORTEX Visitor" — both should still be routed to checkout
// by marketing links, so guards must match the PAID names, not exclude
// "Visitor" (that substring check silently blocked every free user).
const PAID_PLAN_NAMES = ["ORTEX Basic", "ORTEX Advanced", "ORTEX Institutional"];
const isPaidPlan = (plan: string | null | undefined): boolean => !!plan && PAID_PLAN_NAMES.some((p) => plan.includes(p));

const App = ({ Component, pageProps, router: { events, pathname, query } }: AppProps) => {
    // Settings > Date & time: keeps dateParser and the grids on the user's zone (#1281).
    useTimeSettingsSync();
    const [theme, setTheme] = useAtom(themeState);
    const [contrast, setContrast] = useAtom(contrastState);
    const [moduleSettings, setModuleSettings] = useAtom(moduleSettingsState);
    const userColorSettings = useAtomValue(userColorSettingsState);
    // Colour atoms as they were before high contrast was applied (user settings
    // or defaults), so switching it off restores them exactly.
    const contrastBaseRef = useRef<Record<string, string> | null>(null);
    const contrastAppliedRef = useRef(false);
    const contrastUserRef = useRef<unknown>(undefined);
    const CONTRAST_ATOMS = {
        ortexColor: ortexColorState,
        lighterOrtexColor: lighterOrtexColorState,
        positive: positiveState,
        negative: negativeState,
        grey: greyState,
        blackMedium: blackMediumState,
        darkDefault: darkDefaultState,
        darkSurfaceDefault: darkSurfaceDefaultState,
        iconColor: iconColorState,
        dividerDark: dividerDarkState,
        borderDark: borderDarkState,
        popoverContent: popoverContentState,
    } as const;
    // Read and written through the store, not hooks: these run inside effects and must not subscribe.
    const store = useStore();
    const readContrastColors = useCallback((): Record<string, string> => {
        const out: Record<string, string> = {};
        for (const [k, a] of Object.entries(CONTRAST_ATOMS)) out[k] = store.get(a);
        return out;
    }, [store]);
    const writeContrastColors = useCallback(
        (c: Record<string, string>) => {
            for (const [k, a] of Object.entries(CONTRAST_ATOMS)) if (c[k]) store.set(a, c[k]);
        },
        [store]
    );
    // Logo URLs carry the theme so the logo service serves artwork that reads on it.
    useEffect(() => {
        setLogosTheme(theme);
    }, [theme]);
    const userData = useAtomValue(userDataState);
    const userDataRef = useRef(userData);
    const prevUserIdRef = useRef<number | undefined>(undefined);
    const pathnameRef = useRef(pathname);
    const ortexColor = useAtomValue(ortexColorState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const grey = useAtomValue(greyState);
    const neutral = useAtomValue(neutralState);
    const textColor = useAtomValue(textColorState);
    const greyMedium = useAtomValue(greyMediumState);
    const greyDarker = useAtomValue(greyDarkerState);
    const subtextGrey = useAtomValue(subtextGreyState);
    const greyLighter = useAtomValue(greyLighterState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const darkerBackgroundColor = useAtomValue(darkerBackgroundColorState);
    const darkSurfaceDefaultColor = useAtomValue(darkSurfaceDefaultState);
    const newbackgroundColor = useAtomValue(newbackgroundColorState);
    const universes = useAtomValue(universesState);
    const whiteColor = useAtomValue(whiteState);
    const setUniverse = useSetAtom(universeState);
    const user = useAtomValue(userState);
    const [customViews, setCustomViews] = useAtom(customViewsState);
    const [unsavedChanges, setUnsavedChanges] = useAtom(customViewsUnsavedState);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
    const currentViewData = useAtomValue(customViewsCurrentViewDataState);
    const [urlToGo, setUrlToGo] = useState(null);

    const subscription = useAtomValue(subscriptionState);
    const [loading, setLoading] = useState(true);
    // True until the first client-side navigation: the HTML on screen came from the server.
    const [serverPage, setServerPage] = useState(true);
    const [offerings, setOfferings] = useAtom(subscriptionOfferingsState);

    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();

    const setNotificationsList = useSetAtom(notificationsListState);
    const stockTradingItemId = useAtomValue(stockTradingItemIdState);

    const { asPath, isReady, push, replace } = useRouter();

    // WebMCP tools for AI agents in the browser (src/lib/webmcp.ts), once the page has started up.
    useEffect(() => {
        let unregister = () => {};
        const cancel = scheduleIdle(() => {
            unregister = registerSiteTools((path) => push(path));
        });
        return () => {
            cancel();
            unregister();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useAndroidBackButton();
    // undefined while the user loads, so the device is registered once with the right user.
    const pushAuthKey = isUndefined(userData) ? undefined : String(userData?.user_id ?? "anon");
    usePushNotifications(push, pushAuthKey);

    const [showAppSubscriberModal, setShowAppSubscriberModal] = useAtom(showAppSubscriberModalState);
    const [showAgreementsModal, setShowAgreementsModal] = useAtom(showAgreementsModalState);
    const [chatModal, setChatModal] = useAtom(chatModalState);
    const paymentModalOpen = useAtomValue(paymentModalOpenState);

    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const isAuthFormRoute = AUTH_FORM_ROUTES.includes(pathname);
    // Read after mount so the server HTML and the first client render agree (both: no session).
    const [hasLocalSession, setHasLocalSession] = useState(false);
    useEffect(() => {
        if (isAuthFormRoute) setHasLocalSession(hasStoredCognitoSession());
    }, [isAuthFormRoute]);

    // T&C suppression: while the payment modal is open, and for a couple of page
    // views after it closes, we hide the agreements popup so it can't cover the
    // checkout (or the win-back / discount modal that may appear right after the
    // user decides not to pay).
    const paymentModalOpenRef = useRef(false);
    const [tcCooldownViews, setTcCooldownViews] = useState(0);

    const [snackBar, setSnackBar] = useState<{
        open: boolean;
        text: React.ReactNode;
        severity: "success" | "error" | "info" | "warning";
        action?: { label: string; onClick: () => void };
        autoHideDuration?: number;
    }>({
        open: false,
        text: "",
        severity: "success",
    });

    // Built once per colour change, not on every render of the app: a new theme object makes every
    // MUI component below re-render and recompute its styles, and _app re-renders often at start-up.
    const { light, dark } = useMemo(
        () =>
            getThemes({
                ortexColor,
                positive,
                negative,
                neutral,
                textColor,
                greyMedium,
                greyLighter,
                backgroundColor,
                darkerBackgroundColor,
                darkSurfaceDefaultColor,
                newbackgroundColor,
                whiteColor,
            }),
        [ortexColor, positive, negative, neutral, textColor, greyMedium, greyLighter, backgroundColor, darkerBackgroundColor, darkSurfaceDefaultColor, newbackgroundColor, whiteColor]
    );

    // The social-login plugin is initialized once the page has started up (src/lib/socialLogin.ts).
    useEffect(() => scheduleIdle(() => socialLogin().catch(() => undefined)), []);

    useEffect(() => {
        if (query.message === "welcome" && userData?.first_name) {
            setSnackBar({ open: true, text: `${userData.first_name}, welcome to ORTEX!`, severity: "success" });
        }
    }, [query.message, userData?.first_name]);

    // Keep refs in sync so Hub listener closures always see latest values
    useEffect(() => {
        userDataRef.current = userData;
    }, [userData]);

    useEffect(() => {
        pathnameRef.current = pathname;
    }, [pathname]);

    // Push user_id to dataLayer on sign-in / sign-out
    useEffect(() => {
        const userId = userData?.user_id;
        // Mirror it into the tracking module so events fired from plain click
        // handlers (outside any atom consumer) still carry the user.
        setTrackingUser(userId ?? null, userData?.email ?? null);
        if (userId) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ user_id: userId });
        } else if (prevUserIdRef.current) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ user_id: null });
        }
        prevUserIdRef.current = userId;
    }, [userData?.user_id, userData?.email]);

    const amplifySignOut = async (vertexUser, ortexUser) => {
        clearPendingPassword();
        try {
            await signOutFromBackend(vertexUser, ortexUser);
            const { signOut } = await import("@/lib/amplify");
            await signOut();
        } catch (error) {
            console.error("Error signing out user", error);
        } finally {
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith("CognitoIdentityServiceProvider") || key.startsWith("amplify-")) {
                    localStorage.removeItem(key);
                }
            });
            localStorage.removeItem("vertexUser");
            localStorage.removeItem("ortexUser");
        }
    };

    useEffect(() => {
        if (query.hasOwnProperty("promo")) {
            query.promo ? sessionStorage.setItem("promoCode", query.promo as string) : sessionStorage.setItem("promoCode", "true");
        }
    }, [query.message, query.promo]);

    // `?ai=open` deep-links the ORTEX AI chat modal. Used by other parts of
    // the product (e.g. the cancellation flow's "have you tried our AI
    // Assistant?" card) so a single click can drop the user straight into
    // the chat without making them hunt for the header button. We strip
    // the query param after opening so a refresh doesn't keep re-opening
    // the modal.
    useEffect(() => {
        if (query.ai === "open") {
            setChatModal({ open: true, initialMessage: undefined, startingModel: undefined, chatModel: undefined });
            const { ai: _ai, ...rest } = query;
            replace({ pathname, query: rest }, undefined, { shallow: true });
        }
    }, [query.ai]);

    // Backward-compat: `?checkout=true` (or `?v=&t=` with a plan and
    // interval pre-selected) used to pop the payment modal when users
    // landed on /upgrade — that page does handleAppSubscriberUpgrade()
    // before redirecting to /. If a marketing link lands on a different
    // path (root, markets-overview, anywhere) the trigger never fired
    // and the user just sat on the page wondering why the checkout
    // didn't appear. This effect gives any path the same behaviour.
    //
    // Skips on the auth pages (the visitor signup flow has its own
    // payment trigger), when the modal is already open, and for
    // visitor / not-yet-loaded user state. Plain query.checkout reads
    // are guarded behind a `user` truthy check so we don't pre-fetch
    // subscription data for anonymous traffic.
    const wantsCheckoutFromUrl = query.checkout === "true" || (!!query.v && !!query.t);
    useEffect(() => {
        if (!wantsCheckoutFromUrl) return;
        if (!user || !user.subscription_plan) return;
        if (paymentModalOpen) return;
        if (subscription?.plans?.length) return;
        const skipPaths = ["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect", "/upgrade"];
        if (skipPaths.includes(pathname)) return;
        // A PAYING subscriber should never be pushed into checkout by a
        // marketing link — a TRYAI/teaser URL just lands them in the app.
        // NOTE: registered free users have subscription_plan "ORTEX Free"
        // (the "Visitor" plan is only the anonymous fallback), so the guard
        // must check the paid plan names explicitly — a "Visitor" substring
        // check blocks the entire free-user audience of the link.
        if (isPaidPlan(user.subscription_plan)) {
            clearCheckoutIntent();
            return;
        }
        // Anonymous visitors can't check out (the subscription endpoint has no
        // Stripe customer to bill and returns a plan-less payload, which used to
        // crash the payment modal). Send them to create an account instead, with
        // the checkout intent saved — after signup the intent-replay effect below
        // re-applies promo/plan and pops checkout automatically.
        if (!user.email || user.email === "visitor@ortex.com") {
            saveCheckoutIntent({ promo: typeof query.promo === "string" && query.promo !== "true" ? query.promo : undefined, t: query.t as string, v: query.v as string });
            replace("/register");
            return;
        }
        // Open modal + load subscription in the same call as /upgrade does.
        handleAppSubscriberUpgrade({ openPaymentModal: true });
    }, [wantsCheckoutFromUrl, user?.subscription_plan, user?.email, pathname, paymentModalOpen, subscription?.plans?.length]);

    // Persistent checkout intent (belt-and-braces — see src/lib/checkoutIntent.ts).
    //
    // Affiliate / marketing links carry `?checkout=true&promo=…`, but the social
    // OAuth round-trip + multi-hop login forwards (login → upgrade → home) strip
    // the query before the user lands, so the URL-driven trigger above never
    // fires. We stash the intent (localStorage + sessionStorage + cookie) the
    // moment we see `checkout=true` — the login page also saves it BEFORE its
    // social redirect — then re-apply it to the URL once the user lands
    // authenticated on a normal page. Covers "show checkout after a new signup"
    // and "show it again next login until they pay". The intent persists across
    // sessions (cleared once they have a paid plan); a per-session marker stops
    // it nagging on every navigation.
    useEffect(() => {
        if (query.checkout !== "true") return;
        saveCheckoutIntent({ promo: query.promo as string, t: query.t as string, v: query.v as string });
    }, [query.checkout, query.promo, query.t, query.v]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        // Until the router is ready, asPath on a dynamic route can still be the route template.
        if (!isReady) return;
        const intent = loadCheckoutIntent();
        if (!intent) return;
        if (!user || !user.subscription_plan) return;
        // Only for an authenticated (registered / logged-in) user — never pop
        // checkout at an anonymous visitor who merely touched an affiliate link.
        if (!user.email || user.email === "visitor@ortex.com") return;
        const skipPaths = ["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect", "/upgrade"];
        if (skipPaths.includes(pathname)) return;
        // Guardrail: a PAID subscriber should never be pushed into checkout.
        // Free users ("ORTEX Free" — not "Visitor", which is the anonymous
        // fallback plan) SHOULD get the replay: that's the whole point of
        // persisting the intent through signup.
        if (isPaidPlan(user.subscription_plan)) {
            clearCheckoutIntent();
            return;
        }
        // Already shown this session, or the URL already carries checkout → let
        // the URL-driven effect above handle it.
        if (sessionStorage.getItem(CHECKOUT_SHOWN_KEY) === "1") return;
        if (query.checkout === "true") return;
        sessionStorage.setItem(CHECKOUT_SHOWN_KEY, "1");
        replace(checkoutReplayHref(asPath, intent), undefined, { shallow: true });
    }, [user?.subscription_plan, pathname, query.checkout, isReady]);

    // Robustly honor the affiliate post-login destination (ortex_post_login). The
    // signedIn Hub handler consumes it inside saveAuthTokens().then(), which
    // silently misses whenever saveAuthTokens rejects, no fresh signedIn event
    // fires (an existing Cognito session is reused), or it loses a redirect race —
    // leaving the user on the home page with the destination unconsumed. This
    // re-checks it on every authenticated landing, so the upgrade/checkout flow
    // still happens. ortex_post_login is only ever set by the affiliate social
    // deep-link, so this is a no-op for every normal login.
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!user || !user.email || user.email === "visitor@ortex.com") return;
        const skipPaths = ["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect", "/upgrade"];
        if (skipPaths.includes(pathname)) return;
        const dest = sessionStorage.getItem("ortex_post_login");
        if (!dest) return;
        sessionStorage.removeItem("ortex_post_login");
        // Absolute URLs (e.g. an MCP connect return to ortex.com) need a real
        // cross-origin navigation; internal paths use the router. Only ever follow
        // an absolute URL that is ORTEX-owned (prevents open-redirect abuse).
        if (/^https?:\/\//i.test(dest)) {
            try {
                const u = new URL(dest);
                if (u.hostname === "ortex.com" || u.hostname.endsWith(".ortex.com")) {
                    window.location.href = dest;
                }
            } catch {
                /* ignore malformed */
            }
        } else {
            void replace(dest);
        }
    }, [user?.email, pathname]);

    // Track payment-modal open/close to drive the T&C cooldown (see state above).
    useEffect(() => {
        if (!paymentModalOpen && paymentModalOpenRef.current) {
            setTcCooldownViews(2);
        }
        paymentModalOpenRef.current = paymentModalOpen;
    }, [paymentModalOpen]);

    useEffect(() => {
        if (paymentModalOpen) return;
        setTcCooldownViews((n) => (n > 0 ? n - 1 : 0));
    }, [pathname]);

    const suppressTandcPopup = paymentModalOpen || tcCooldownViews > 0;

    // The address from the ID token, for the sign_up event: userData is not loaded yet that
    // soon after sign-in, and the visitor placeholder it would hold is useless to the ad tags.
    const idTokenEmailRef = useRef<string | null>(null);

    const saveAuthTokens = async () => {
        try {
            const { fetchAuthSession } = await import("@/lib/amplify");
            const { tokens } = await fetchAuthSession();
            const idToken = tokens?.idToken?.toString();
            const claimedEmail = tokens?.idToken?.payload?.email;
            idTokenEmailRef.current = typeof claimedEmail === "string" ? claimedEmail : null;

            if (!idToken) {
                throw new Error("No idToken in session");
            }

            // const vertexUrl = `${process.env.NEXT_PUBLIC_API_URL_VERTEX}/users/auth/cognito/`;
            const ortexUrl = `${process.env.NEXT_PUBLIC_API_URL}/users/auth/cognito/`;

            // const vertexConfig = {
            //     withCredentials: true,
            //     xsrfCookieName: "vertex_csrftoken",
            //     headers: { "Content-Type": "application/json" },
            //     params: null,
            // };

            const ortexConfig = {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                },
                params: null,
            };

            const [ortexSettled] = await Promise.allSettled([axios.post(ortexUrl, idToken, ortexConfig)]);

            const failureReasons: string[] = [];

            // if (vertexSettled.status === "fulfilled") {
            //     const vertexUserId = vertexSettled.value?.data?.userId;
            //     if (vertexUserId) {
            //         localStorage.setItem("vertexUser", vertexUserId);
            //     } else {
            //         const reason = "Vertex: missing userId in response";
            //         console.error(reason, vertexSettled.value?.data);
            //         failureReasons.push(reason);
            //     }
            // } else {
            //     const reason = `Vertex: request failed (${vertexSettled.reason?.message ?? vertexSettled.reason})`;
            //     console.error(reason, vertexSettled.reason);
            //     failureReasons.push(reason);
            // }

            if (ortexSettled.status === "fulfilled") {
                const ortexUserId = ortexSettled.value?.data?.userId;
                if (ortexUserId) {
                    localStorage.setItem("ortexUser", ortexUserId);
                } else {
                    const reason = "Ortex: missing userId in response";
                    console.error(reason, ortexSettled.value?.data);
                    failureReasons.push(reason);
                }
            } else {
                const reason = `Ortex: request failed (${ortexSettled.reason?.message ?? ortexSettled.reason})`;
                console.error(reason, ortexSettled.reason);
                failureReasons.push(reason);
            }

            if (failureReasons.length > 0) {
                throw new Error(failureReasons.join("; "));
            }
        } catch (error) {
            console.error("Error saving auth tokens:", JSON.stringify(error));
            throw error;
        }
    };

    useGetUserData(isAuthenticated);

    // Auth events from Amplify, which the sign-in features load (src/lib/authEvents.ts).
    useEffect(() => {
        const hubListenerCancel = setAuthEventHandler(async (data) => {
            const { payload } = data;
            const vertexUser = localStorage.getItem("vertexUser");
            const ortexUser = localStorage.getItem("ortexUser");

            switch (payload.event) {
                case "signedIn":
                    saveAuthTokens()
                        .then(() => {
                            setIsAuthenticated(true);
                            if (["/login", "/login-legacy", "/cognito-login", "/register", "/auth-redirect"].includes(pathnameRef.current)) {
                                void replace("/");
                            }
                            if (sessionStorage.getItem("isNewSignup") === "true") {
                                sessionStorage.removeItem("isNewSignup");
                                // userData is usually not loaded yet this soon after sign-in;
                                // saveAuthTokens has just stored the backend user id though
                                // (it can also hold the literal "true" on some social paths).
                                const storedUserId = Number(localStorage.getItem("ortexUser"));
                                trackSignUp(userDataRef.current?.user_id ?? (Number.isInteger(storedUserId) && storedUserId > 0 ? storedUserId : null), idTokenEmailRef.current ?? userDataRef.current?.email ?? null);
                                handleAppSubscriberUpgrade({ openPaymentModal: true });
                            }
                        })
                        .catch((error) => {
                            console.error("Error saving auth tokens:", error);
                            setSnackBar({
                                open: true,
                                text: `Sign-in error: ${error?.message ?? "unknown error"}. Please try again or contact support.`,
                                severity: "error",
                            });
                        });
                    break;
                case "signInWithRedirect_failure":
                    // Clear any stashed affiliate destination so an abandoned/failed
                    // social sign-in can't redirect a later normal login to /upgrade.
                    sessionStorage.removeItem("ortex_post_login");
                    amplifySignOut(vertexUser, ortexUser);

                    setSnackBar({ open: true, text: "Sign in failed. Please try again.", severity: "error" });
                    break;
                default:
                    break;
            }
        });

        return () => {
            hubListenerCancel();
        };
    }, [handleAppSubscriberUpgrade, replace]);

    // Amplify itself is loaded where it is used. Here that is the Hosted UI (Google / Apple) return: on
    // /auth-redirect, or while a redirect sign-in is still in flight, its listener exchanges the code for tokens.
    useEffect(() => {
        if (pathname === "/auth-redirect" || hasInflightOAuth()) void import("@/lib/amplify");
    }, [pathname]);

    //notifications
    useNotificationsFetcher(asPath, stockTradingItemId, setNotificationsList);

    //subscription config
    usePaymentConfig(user, offerings, setOfferings);

    const userIdle = useIdle(FOUR_HOURS);

    const { multiWebSocket, ortexWebSocket } = useAppWebSockets(userIdle);
    // A fresh object here re-rendered every consumer (every live grid cell) on any _app render.
    const socketContext = useMemo(() => ({ multiWebSocket, ortexWebSocket }), [multiWebSocket, ortexWebSocket]);

    useEffect(() => {
        if (userIdle) push("/idle");
    }, [userIdle]);

    let Layout = RootLayout;

    if (["/login", "/login-legacy", "/cognito-login", "/iframes", "/register", "/auth-redirect"].includes(pathname)) Layout = AuthLayout;

    useEffect(() => {
        const showRateLimited = (response: { data?: unknown; headers?: unknown }) => {
            const { text, contactSupport } = rateLimitMessage(retryAfterSeconds(response.data, response.headers));
            const [before, after] = text.split("Terms and Conditions");
            setSnackBar({
                open: true,
                text:
                    after === undefined ? (
                        text
                    ) : (
                        <>
                            {before}
                            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
                                Terms and Conditions
                            </a>
                            {after}
                        </>
                    ),
                severity: "error",
                autoHideDuration: 15000,
                action: contactSupport
                    ? {
                          label: "Email support",
                          onClick: () => {
                              window.location.href = `mailto:${SUPPORT_EMAIL}`;
                          },
                      }
                    : undefined,
            });
        };
        axios.interceptors.response.use(
            // validateStatus is `status < 500`, so most 429s resolve and land here, not in the error handler.
            (res) => {
                if (res.status === 429) showRateLimited(res);
                return res;
            },
            (err) => {
                const { config, message } = err.toJSON();
                // Sign-out failures stay silent. Matched on the path because the URL is absolute
                // when NEXT_PUBLIC_API_URL is set: there the logout's redirect chain ends on
                // app.ortex.com, which the browser blocks as cross-origin, so it always "fails".
                const isLogout = /\/logout\/?$/.test((config.url ?? "").split("?")[0]);
                if (message !== "canceled" && !isLogout) {
                    const status = err.response?.status;
                    if (status === 429) {
                        showRateLimited(err.response);
                        throw err;
                    }
                    const text = `Service error${status ? ` (${status})` : ""}. Please try again later.${
                        process.env.NEXT_PUBLIC_VERCEL_ENV
                            ? ""
                            : `
                        ${message} - ${config.method.toUpperCase()} at:
                        ${config.url}`
                    }`;
                    setSnackBar({ open: true, text, severity: "error" });
                }
                throw err;
            }
        );
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        setTheme(["dark", "light"].includes(savedTheme) ? (savedTheme as "dark" | "light") : "dark");
    }, []);

    const onRouteChangeStart = useCallback(
        (url) => {
            if (pathname.includes("/custom-views") && unsavedChanges) {
                setUrlToGo(url);
                setShowUnsavedChangesModal(true);
                throw new Error("Unsaved changes");
            } else {
                setLoading(true);
                setServerPage(false);
            }
        },
        [unsavedChanges]
    );

    useEffect(() => {
        events.on("routeChangeStart", onRouteChangeStart);

        return () => {
            events.off("routeChangeStart", onRouteChangeStart);
        };
    }, [onRouteChangeStart]);

    // First-party click-id capture (?oppref=) and OpenAI page_viewed, on load and on
    // every client-side navigation. See trackRouteView in src/lib/tracking.ts.
    useEffect(() => {
        trackRouteView();
        events.on("routeChangeComplete", trackRouteView);
        return () => {
            events.off("routeChangeComplete", trackRouteView);
        };
    }, [events]);

    useEffect(() => {
        const routeChangeStart = () => setLoading(true);
        const routeChangeError = () => setLoading(false);
        const routeChangeComplete = () => setLoading(false);

        events.on("routeChangeError", routeChangeError);
        events.on("routeChangeComplete", routeChangeComplete);

        return () => {
            events.off("routeChangeStart", routeChangeStart);
            events.off("routeChangeError", routeChangeError);
            events.off("routeChangeComplete", routeChangeComplete);
        };
    }, [user]);

    // Terms & Conditions consent modal — gated to NOT open while a payment
    // flow is active. Previously the consent dialog (MUI Dialog, z=1300)
    // popped on top of the payment Drawer (z=1200) for fresh signups,
    // forcing the user to agree before they could even see the payment
    // form. Paying for a Stripe subscription is itself a T&C acceptance
    // (the Stripe Checkout consent_collection block in
    // create_checkout_session_subscription enforces this), so deferring
    // our own modal until after payment doesn't open any legal exposure.
    //
    // Deps include both `paymentModalOpen` (set by our app code when a
    // user clicks Subscribe) AND `subscription?.plans?.length` (Payment
    // also renders when the subscription state has plans loaded — see
    // the Payment render condition above). When either becomes truthy
    // we suppress the agreements modal; when both clear (post-payment
    // success or user closes the drawer) the effect re-fires and the
    // modal opens for users whose T&C still hasn't been accepted.
    useEffect(() => {
        const paymentInProgress = paymentModalOpen || !!subscription?.plans?.length;
        if (paymentInProgress) {
            setShowAgreementsModal(false);
            return;
        }
        // Signed-out visitors get no dialog: the cookie notice carries the Terms line instead. A dialog over
        // the page on arrival is an intrusive interstitial to Google, and it added start-up work.
        setShowAgreementsModal(!user?.subscription_plan?.includes("Visitor") && !!user.ortex_tandc && user.ortex_tandc !== "agreed");
    }, [user, paymentModalOpen, subscription?.plans?.length]);

    useEffect(() => {
        if (pathname.includes("/custom-views")) {
            const beforeUnload = (e: BeforeUnloadEvent) => {
                if (unsavedChanges) {
                    e.preventDefault();
                    e.returnValue = "";
                }
            };

            window.addEventListener("beforeunload", beforeUnload);

            return () => {
                window.removeEventListener("beforeunload", beforeUnload);
            };
        }
    }, [unsavedChanges]);

    const handleAgree = () => {
        // "Save and leave" has to actually persist. This used to only copy the
        // working data into the atoms, which survived the navigation but not a
        // reload — the user was told their view was saved when it was not
        // (ortex-gui#1195).
        saveCustomViews(currentViewData).catch(() => undefined);
        setImmediate(() => {
            setUnsavedChanges(false);
            urlToGo && push(urlToGo);
        });
        setShowUnsavedChangesModal(false);
        setCustomViews(currentViewData);
        setUrlToGo(null);
    };

    const handleContinueWithoutSaving = () => {
        setImmediate(() => {
            urlToGo && push(urlToGo);
        });
        setUnsavedChanges(false);
        setShowUnsavedChangesModal(false);
        setUrlToGo(null);
    };

    const [flattenedUniverses, setFlattenedUniverses] = useState<Universe[]>([]);

    useEffect(() => {
        if (universes && universes.selected !== undefined) {
            const flattened = Object.values(universes?.regions)
                .reduce((acc, val) => acc.concat(val), [])
                .concat(universes.portfolios);

            setFlattenedUniverses(flattened);
        }
    }, [universes]);

    useEffect(() => {
        if (query.uni_id && flattenedUniverses.length) setUniverse(flattenedUniverses.find((universe) => universe.id === Number(query.uni_id)));
    }, [query, universes]);

    const { userLoading } = useGetUserData();

    useEffect(() => {
        setLoading(userLoading);

        if (user.overdue && (!isOrtexApp() || offerings)) {
            setSnackBar({ open: true, text: "Your last payment failed. Please update your card details to continue to use ORTEX. We will not charge you for previous overdue invoices.", severity: "error" });
        }
    }, [userLoading]);

    useEffect(() => {
        // Don't run while a Hosted-UI OAuth redirect is completing — Amplify keeps the
        // PKCE verifier / inflight flag in CognitoIdentityServiceProvider.* localStorage
        // keys, and wiping them here aborts the code-for-token exchange (stuck spinner).
        if (pathname === "/auth-redirect") return;
        // Nor straight after a sign-in in this tab. `user` still holds the visitor fetched on
        // /login until useGetUserData reloads it, while the sign-in has already moved the route
        // to "/": wiping here then deletes the tokens Amplify has just stored. ORTEX carries on
        // through its session cookie, but every Vertex request goes out without a token.
        if (isAuthenticated) return;
        if (user?.subscription_plan === "ORTEX Visitor") {
            Object.keys(localStorage).forEach((key) => {
                if (/\.(oauthPKCE|oauthState|inflightOAuth)$/.test(key)) return; // preserve in-flight OAuth
                if (key.startsWith("CognitoIdentityServiceProvider") || key.startsWith("amplify-")) {
                    localStorage.removeItem(key);
                }
            });
        }
    }, [user, pathname, isAuthenticated]);

    // Expose the active theme on <html> so plain CSS (redesign.scss) can scope dark-mode rules.
    useEffect(() => {
        if (theme) document.documentElement.setAttribute("data-theme", theme);
    }, [theme]);

    // Display contrast (normal | high). First paint uses the browser mirror;
    // ?contrast=high|normal is treated like flipping the switch (persisted once
    // the user is known). The saved accessibility setting below is the source of truth.
    const contrastFromUrlRef = useRef<"normal" | "high" | null>(null);
    useEffect(() => {
        const fromUrl = new URLSearchParams(window.location.search).get("contrast");
        if (fromUrl === "high" || fromUrl === "normal") {
            contrastFromUrlRef.current = fromUrl;
            storeContrast(fromUrl);
        }
        setContrast(readStoredContrast());
    }, []);

    // Settings > Accessibility > High Contrast (module_settings) wins whenever it
    // is defined; keep the browser mirror in step. A URL choice made on this
    // load is written to the account once, then the setting takes over.
    useEffect(() => {
        if (!userData) return;
        const isVisitor = userData.subscription_plan === "ORTEX Visitor";
        if (contrastFromUrlRef.current) {
            const chosen = contrastFromUrlRef.current;
            contrastFromUrlRef.current = null;
            persistContrastSetting(chosen, isVisitor, setModuleSettings);
            setContrast(chosen);
            return;
        }
        const saved = moduleSettings?.[CONTRAST_SETTING_KEY]?.value;
        if (saved === undefined) return;
        const next = saved ? "high" : "normal";
        storeContrast(next);
        setContrast(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, moduleSettings]);

    // Mirror the preference on <html> (redesign.scss keys the CSS tokens off it)
    // and swap the colour atoms so charts and inline colours follow.
    // Re-runs when the user loads, because userState re-seeds the atoms from
    // the saved settings at that point.
    useEffect(() => {
        document.documentElement.setAttribute("data-contrast", contrast);
        const high = contrast === "high" && theme === "dark";
        const userChanged = contrastUserRef.current !== userData;
        contrastUserRef.current = userData;
        if (high) {
            if (!contrastAppliedRef.current || userChanged) contrastBaseRef.current = readContrastColors();
            // Manual colour choices win in every mode: only untouched colours
            // get the high-contrast values.
            const hc: Record<string, string> = { ...HIGH_CONTRAST_COLORS };
            (["positive", "negative", "grey", "blackMedium"] as UserColorKey[]).forEach((k) => {
                if (isManualColor(k, userColorSettings[k])) hc[k] = userColorSettings[k] as string;
            });
            writeContrastColors(hc);
            contrastAppliedRef.current = true;
        } else if (contrastAppliedRef.current) {
            if (contrastBaseRef.current) writeContrastColors(contrastBaseRef.current);
            contrastAppliedRef.current = false;
            contrastBaseRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contrast, theme, userData]);

    // Memoized so the app's own re-renders do not re-render the page. The hydration boundary around
    // both is in withApp (lib/app-hoc.tsx).
    const pageElement = useMemo(() => <Component {...pageProps} />, [Component, pageProps]);

    // The layout (header, menus, ticker tape, stock header) is memoized on what it is given here, for the
    // reason the hosts above are: it re-renders for its own atoms, not for every _app render.
    const showLoader = (isUndefined(userData) || loading) && !(isAuthFormRoute && !hasLocalSession);
    const holdLoader = serverPage && !isSignedInOnlyRoute(pathname);
    const showPage = user.email !== "visitor1@ortex.com" || PUBLIC_ROUTES.includes(pathname);
    const layoutElement = useMemo(
        () => (
            <Layout {...pageProps}>
                {showLoader &&
                    (holdLoader ? (
                        // First load of a server-rendered page: see src/lib/loaderHold.ts.
                        <div className={LOADER_HELD_CLASS}>
                            <BrandLoading {...pageProps} pathname={pathname} />
                        </div>
                    ) : (
                        <BrandLoading {...pageProps} pathname={pathname} />
                    ))}
                {showPage && pageElement}
            </Layout>
        ),
        [Layout, pageProps, showLoader, holdLoader, showPage, pathname, pageElement]
    );
    const setSnackBarOpen = useCallback((open: boolean) => setSnackBar((current) => ({ ...current, open })), []);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme === "dark" ? dark : light}>
                <AppCssBaseline />
                <Context.Provider value={socketContext}>
                    <style jsx global>{`
                        ::-webkit-scrollbar-thumb {
                            border: 4px solid ${isUndefined(theme) ? "unset" : backgroundColor};
                            background-color: ${isUndefined(theme) ? "unset" : theme === "dark" ? greyMedium : greyLighter};
                        }

                        :root {
                            --table-cell-subtext: ${subtextGrey};
                            --table-cell-consensus-sell: ${negative};
                            --table-cell-consensus-sell-shade: ${brighten(negative, -0.15)};
                            --table-cell-consensus-hold: ${greyMedium};
                            --table-cell-consensus-hold-shade: ${greyDarker};
                            --table-cell-consensus-buy: ${positive};
                            --table-cell-consensus-buy-shade: ${brighten(positive, -0.15)};
                        }

                        html {
                            color: ${isUndefined(theme) ? "unset" : textColor};
                            scrollbar-color: ${isUndefined(theme) ? "unset" : theme === "dark" ? greyMedium : greyLighter} transparent;
                        }
                        .positive {
                            color: ${positive};
                        }
                        .bg-positive {
                            background-color: ${positive};
                        }
                        .border-positive {
                            border: solid;
                            border-color: ${positive};
                        }
                        .negative {
                            color: ${negative};
                        }
                        .bg-negative {
                            background-color: ${negative};
                        }
                        .bc-negative {
                            border-color: ${negative};
                        }
                        .bc-positive {
                            border-color: ${positive};
                        }
                        .border-negative {
                            border: solid;
                            border-color: ${negative};
                        }
                        .grey {
                            color: ${grey};
                        }
                        .neutral {
                            color: ${neutral};
                        }
                        .bg-neutral {
                            background-color: ${neutral};
                        }
                        .border-neutral {
                            border: solid;
                            border-color: ${neutral};
                        }
                        form iframe {
                            margin: 0 !important;
                            width: 100% !important;
                        }
                        .MuiBackdrop-root {
                            background-color: rgba(0, 0, 0, 0.5);
                        }
                        .MuiDataGrid-paper {
                            background-color: ${theme === "dark" ? "rgb(48 67 77)" : backgroundColor};
                        }

                        .MuiDataGrid-menu .MuiPaper-root {
                            background-color: ${theme === "dark" ? "rgb(48 67 77)" : backgroundColor};
                        }

                        .MuiDateRangePickerViewDesktop-root {
                            background-color: ${theme === "dark" ? "rgb(48 67 77)" : backgroundColor};
                        }

                        .MuiDateRangePickerViewDesktop-root .MuiSvgIcon-root {
                            color: ${theme === "dark" ? "#FFF" : "#000"};
                        }
                        .MuiDateRangePickerViewDesktop-root .defaultCalendarMonth {
                            color: "green";
                        }

                        .MuiToggleButtonGroup-grouped {
                            border-color: ${theme === "dark" ? "rgba(230,230,230, 0.5)" : null};
                        }

                        .MuiDialog-paper {
                            @media screen and (min-width: 3840px) {
                                max-width: 1400px;
                            }
                        }
                    `}</style>
                    {/* GA4 (G-4PM7DXFDTE) is configured by the GTM container's Google tag, page views
                        and history changes included. A second gtag.js for the same id here cost another
                        ~190 KB and its start-up work on every page; GA only ever sent one page_view. */}
                    {isOrtexApp() && <AppUrlListener />}
                    {/* Overridable social defaults. next/head keeps the last tag per
                        name / key, so a page's own twitter:* or og:image (key="og:image")
                        replaces these instead of being emitted alongside them. */}
                    <Head>
                        {/* Search-engine + AI-overview hints. `max-snippet=-1` lets Google quote
                            the page in full; `max-image-preview=large` is the signal for rich-image
                            eligibility. Lives here (not _document) so a page's own robots meta
                            (key="robots", or any meta name="robots") replaces it. */}
                        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" key="robots" />
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:image" content="https://app.ortex.com/icons/ogimage.png" />
                        <meta property="og:image" content="https://app.ortex.com/icons/ogimage.png" key="og:image" />
                    </Head>
                    {layoutElement}
                    {isOrtexApp() && <PlanPickerDrawer />}
                    <AltDataUpgradeDialog />
                    {!PUBLIC_ROUTES.includes(pathname) && <TrialFullAccessNotice />}
                    {(!!subscription?.plans?.length || paymentModalOpen) && !user.app_subscriber_only && !isOrtexApp() && <Payment />}
                    <CustomViewsUnsavedChangesModal show={showUnsavedChangesModal} setShow={setShowUnsavedChangesModal} agree={handleAgree} continueWithoutSaving={handleContinueWithoutSaving} />
                    {showAppSubscriberModal && subscription && !isOrtexApp() && <AppSubscriberModal open={showAppSubscriberModal} setOpen={setShowAppSubscriberModal} />}

                    {/* Google One Tap for a signed-out visitor on any ordinary page (the auth pages mount
                        their own, with the sign-in / create-account wording). Google rate-limits its own
                        prompt after a dismissal, so it does not nag. */}
                    {!detectBot() &&
                        !isOrtexApp() &&
                        !isAuthenticated &&
                        user?.subscription_plan === "ORTEX Visitor" &&
                        !["/login", "/login-legacy", "/cognito-login", "/register", "/auth-redirect", "/idle"].includes(pathname) &&
                        !pathname.startsWith("/iframes") && <GoogleOneTap context="use" floating />}
                    {!detectBot() && user && showAgreementsModal && !suppressTandcPopup && !["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect"].includes(pathname) && (
                        <AgreementsModal open={showAgreementsModal} setOpen={setShowAgreementsModal} user={user} />
                    )}
                    {/* Cancellation loss warning — surfaces for users
                        whose subscription is cancelled and ends within 30
                        days. Self-fetches the cancellation context, decides
                        whether to show, and handles the abort call. Skips
                        on auth pages so we don't show it during signup /
                        login flows. */}
                    {!detectBot() && user && !["/login", "/login-legacy", "/cognito-login", "/register", "/iframes", "/auth-redirect"].includes(pathname) && <CancellationLossWarning />}
                    {/* App-wide tour runner: ?tour=<id> deep links + startTour()
                        (the in-house Appcues replacement). */}
                    {!detectBot() && <GlobalTourLauncher />}
                    {isOrtexApp() && <PushPrePrompt pathname={pathname} ready={!loading && !isUndefined(userData)} />}
                    {isOrtexApp() && <AppUpdateBanner pathname={pathname} ready={!loading && !isUndefined(userData)} />}
                    <SnackBar open={snackBar.open} setOpen={setSnackBarOpen} severity={snackBar.severity} text={snackBar.text} action={snackBar.action} autoHideDuration={snackBar.autoHideDuration} />
                    {/* In-app viewer for SEC filing links (opened via the
                        ortex:open-sec-doc window event from table cells). */}
                    {!detectBot() && <SecFilingModal />}
                    {/* The news story reader every news list opens; ?show=news&id= links to a story. */}
                    {!detectBot() && <NewsStoryModal />}
                    {!detectBot() && <CallDetailsHost />}
                    {chatModal.open && (
                        <ChatModal
                            open={chatModal.open}
                            onClose={() => setChatModal({ open: false, initialMessage: undefined, startingModel: undefined, chatModel: undefined })}
                            initialMessage={chatModal.initialMessage}
                            startingModel={chatModal.startingModel}
                            chatModel={chatModal.chatModel}
                        />
                    )}
                </Context.Provider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default withApp(App);
